/**
 * Source live — **SSE uniquement** (décision 13/06/2026 : le mode statique /
 * poll de fichier a été supprimé). Un serveur (`server/`) pousse les mises à
 * jour via `/api/live/stream`. Filet conservé : le dernier état connu est
 * persistant en localStorage et reste affiché si le flux coupe brièvement
 * (EventSource se reconnecte tout seul).
 */
import { parseLiveJson } from "./liveSchema.ts";
import type { LiveData } from "./liveSchema.ts";
import { parseDerived, type DerivedProbabilities } from "./derived.ts";

export const LIVE_STREAM_URL = "api/live/stream";
const STORAGE_KEY = "wc26.live.lastKnown.v1";

export interface LiveUpdate {
  readonly data: LiveData | null;
  /** Epoch ms du dernier message reçu (null si jamais). */
  readonly fetchedAt: number | null;
  /** True quand le flux est interrompu (on affiche le dernier état connu). */
  readonly degraded: boolean;
  readonly lastError: string | null;
  /** True quand la donnée vient du cache localStorage, pas du flux. */
  readonly fromCache: boolean;
}

type Listener = (update: LiveUpdate) => void;
type DerivedListener = (derived: DerivedProbabilities) => void;

export class LiveClient {
  private listeners: Listener[] = [];
  private derivedListeners: DerivedListener[] = [];
  private stopped = true;
  private es: EventSource | null = null;
  /** True tant que le flux SSE est ouvert (le serveur calcule alors les probas). */
  private sseActive = false;
  /** True entre `new EventSource` et le 1er open/error — évite d'empiler des
   *  ouvertures (refresh + visibilitychange rapides pendant l'établissement). */
  private opening = false;
  private state: LiveUpdate = {
    data: null,
    fetchedAt: null,
    degraded: false,
    lastError: null,
    fromCache: false,
  };

  constructor(private readonly streamUrl: string = LIVE_STREAM_URL) {}

  onUpdate(listener: Listener): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  /** Probabilités calculées par le serveur (event SSE `derived`). */
  onDerived(listener: DerivedListener): () => void {
    this.derivedListeners.push(listener);
    return () => {
      this.derivedListeners = this.derivedListeners.filter((l) => l !== listener);
    };
  }

  get current(): LiveUpdate {
    return this.state;
  }

  /** True tant que le flux pousse (le serveur fournit aussi les probas). */
  streaming(): boolean {
    return this.sseActive;
  }

  start(): void {
    if (!this.stopped) return;
    this.stopped = false;
    const cached = this.loadCache();
    if (cached) {
      this.emit({ data: cached.data, fetchedAt: cached.fetchedAt, degraded: false, lastError: null, fromCache: true });
    }
    document.addEventListener("visibilitychange", this.onVisibility);
    this.openStream();
  }

  stop(): void {
    this.stopped = true;
    this.es?.close();
    this.es = null;
    document.removeEventListener("visibilitychange", this.onVisibility);
  }

  /** Reconnexion manuelle (bouton ↻) ou au retour de focus si le flux est tombé. */
  refresh(): void {
    if (this.stopped || this.opening) return;
    this.reconnect();
  }

  private reconnect(): void {
    this.es?.close();
    this.es = null;
    this.sseActive = false;
    this.openStream();
  }

  private readonly onVisibility = (): void => {
    if (document.visibilityState === "visible" && !this.sseActive && !this.opening) this.reconnect();
  };

  private openStream(): void {
    if (typeof EventSource === "undefined") {
      this.emit({ ...this.state, degraded: true, lastError: "EventSource indisponible" });
      return;
    }
    if (this.opening) return; // une ouverture est déjà en vol
    if (this.es) {
      this.es.close(); // idempotent : jamais deux EventSource en parallèle
      this.es = null;
    }
    this.opening = true;
    try {
      this.es = new EventSource(this.streamUrl);
    } catch {
      this.opening = false;
      this.emit({ ...this.state, degraded: true, lastError: "flux live indisponible" });
      return;
    }
    this.es.addEventListener("open", () => {
      this.opening = false;
      this.sseActive = true;
    });
    this.es.addEventListener("live", (event: MessageEvent<string>) => {
      let raw: unknown;
      try {
        raw = JSON.parse(event.data);
      } catch {
        // frame tronqué/illisible (coupure réseau, proxy) : on garde le dernier
        // état connu et on signale le mode dégradé (règle 8).
        this.emit({ ...this.state, degraded: true, lastError: "frame live illisible" });
        return;
      }
      const { data, error } = parseLiveJson(raw);
      if (!data) {
        this.emit({ ...this.state, degraded: true, lastError: `flux invalide — ${error ?? "?"}` });
        return;
      }
      this.opening = false;
      this.sseActive = true;
      this.saveCache(data);
      this.emit({ data, fetchedAt: Date.now(), degraded: false, lastError: null, fromCache: false });
    });
    this.es.addEventListener("derived", (event: MessageEvent<string>) => {
      let raw: unknown;
      try {
        raw = JSON.parse(event.data);
      } catch {
        return; // frame derived illisible : le worker du navigateur prend le relais
      }
      const derived = parseDerived(raw);
      if (derived) for (const l of this.derivedListeners) l(derived);
    });
    this.es.addEventListener("error", () => {
      // Coupure : EventSource retente tout seul. On garde le dernier état connu
      // à l'écran et on signale le mode dégradé (le filet de sécurité).
      this.opening = false;
      this.sseActive = false;
      this.emit({ ...this.state, degraded: true, lastError: "flux interrompu — reconnexion…" });
    });
  }

  private emit(update: LiveUpdate): void {
    this.state = update;
    for (const l of this.listeners) l(update);
  }

  private saveCache(data: LiveData): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ data, fetchedAt: Date.now() }));
    } catch {
      /* quota/private mode : cache best-effort */
    }
  }

  private loadCache(): { data: LiveData; fetchedAt: number } | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed: unknown = JSON.parse(raw);
      if (typeof parsed !== "object" || parsed === null) return null;
      const { data, fetchedAt } = parsed as { data?: unknown; fetchedAt?: unknown };
      const checked = parseLiveJson(data).data;
      if (!checked || typeof fetchedAt !== "number") return null;
      return { data: checked, fetchedAt };
    } catch {
      return null;
    }
  }
}
